"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.evaluateDose = evaluateDose;
exports.describeGap = describeGap;
exports.isExtraDose = isExtraDose;
const ADVISORY_NOTE = 'This is general information for this medicine, not the instructions from his doctor. ' +
    'The prescribed amount is not known — please check with the pharmacy.';
/**
 * Decide whether logging this dose is worth a gentle question first.
 *
 * Returns undefined when there is nothing to say. Returning a warning is NOT a refusal:
 * the caller shows it, the person answers, and the dose is recorded either way.
 */
function evaluateDose(input) {
    const { med, slotId, priorDosesToday, at, tz } = input;
    const mine = priorDosesToday
        .filter((d) => d.medId === med.medId)
        .sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc));
    if (mine.length === 0)
        return undefined;
    const base = { medId: med.medId, medName: med.name };
    const last = mine[mine.length - 1];
    // 1. This exact slot is already ticked. The most common real mistake: forgetting
    //    that it was already taken, which is precisely what the paper chart existed to
    //    prevent ("ensures you do not take it again because you cannot remember").
    if (slotId != null) {
        const already = mine.find((d) => d.slotId === slotId);
        if (already) {
            return {
                ...base,
                kind: 'SLOT_ALREADY_TAKEN',
                limitSource: 'PRESCRIPTION',
                title: `${med.name} is already logged for this time`,
                body: `It was logged at ${clockOf(already.takenAtUtc, tz)}. Is this an extra dose?`,
                previousTakenAtUtc: already.takenAtUtc,
                minutesSincePrevious: minutesBetween(already.takenAtUtc, at),
            };
        }
    }
    // 2. Already at the daily maximum. Deliberately ahead of the interval check —
    //    "that would be the third today, and the limit is two" is more serious, and
    //    more useful to a person deciding, than "it has only been five hours".
    const max = resolveDailyMax(med);
    if (max && mine.length >= max.count) {
        return {
            ...base,
            kind: 'DAILY_MAX_REACHED',
            limitSource: max.source,
            ...(max.source === 'GENERIC_LABELING' ? { advisoryNote: ADVISORY_NOTE } : {}),
            title: `That would be ${String(mine.length + 1)} today`,
            body: `${med.name} has already been logged ${String(mine.length)} ` +
                `${mine.length === 1 ? 'time' : 'times'} today. ` +
                `${max.source === 'PRESCRIPTION' ? 'The label says' : 'This medicine usually says'} ` +
                `up to ${String(max.count)} a day. Is this an extra dose?`,
            takenTodayCount: mine.length,
            limit: max.count,
            ...(last ? { previousTakenAtUtc: last.takenAtUtc } : {}),
        };
    }
    // 3. Too soon since the previous dose. Checked LAST of the three: when both this
    //    and the daily maximum apply, the daily maximum is the stronger statement.
    const interval = resolveInterval(med);
    if (interval && last) {
        const elapsed = minutesBetween(last.takenAtUtc, at);
        if (elapsed < interval.hours * 60) {
            return {
                ...base,
                kind: 'TOO_SOON',
                limitSource: interval.source,
                ...(interval.source === 'GENERIC_LABELING' ? { advisoryNote: ADVISORY_NOTE } : {}),
                title: `${med.name} was taken recently`,
                body: `The last one was at ${clockOf(last.takenAtUtc, tz)}, ${describeGap(elapsed)} ago. ` +
                    `${interval.source === 'PRESCRIPTION' ? 'The label says' : 'This medicine usually says'} ` +
                    `to leave about ${String(interval.hours)} hours. Is this an extra dose?`,
                previousTakenAtUtc: last.takenAtUtc,
                minutesSincePrevious: elapsed,
                limit: interval.hours,
            };
        }
    }
    return undefined;
}
/** Prescription limits win; generic labeling is the fallback and is labelled as such. */
function resolveInterval(med) {
    if (med.minIntervalHours != null) {
        return { hours: med.minIntervalHours, source: 'PRESCRIPTION' };
    }
    if (med.advisoryMinIntervalHours != null) {
        return { hours: med.advisoryMinIntervalHours, source: 'GENERIC_LABELING' };
    }
    return undefined;
}
function resolveDailyMax(med) {
    if (med.maxPerDay != null)
        return { count: med.maxPerDay, source: 'PRESCRIPTION' };
    if (med.advisoryMaxPerDay != null) {
        return { count: med.advisoryMaxPerDay, source: 'GENERIC_LABELING' };
    }
    return undefined;
}
function minutesBetween(fromIso, to) {
    return Math.max(0, Math.round((to.getTime() - Date.parse(fromIso)) / 60_000));
}
/** "about 40 minutes" / "about 3 hours" — never a bare number of minutes. */
function describeGap(minutes) {
    if (minutes < 60)
        return `about ${String(minutes)} minutes`;
    const hours = Math.round(minutes / 60);
    return `about ${String(hours)} ${hours === 1 ? 'hour' : 'hours'}`;
}
/** Clock time in the patient's zone. Never the runtime's zone. */
function clockOf(iso, tz) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date(iso));
}
/**
 * Whether a logged dose should be recorded as an extra.
 *
 * Derived from the same rules, so the flag on the stored event agrees with whatever
 * the person was shown before they confirmed.
 */
function isExtraDose(input) {
    return evaluateDose(input) !== undefined;
}
//# sourceMappingURL=dosing.js.map